import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Phone, PhoneCall, PhoneMissed, PhoneOff, Clock, Loader2, Plus } from 'lucide-react';
import { LogCallDialog } from '../../pages/CallActivityPage';

const fmtDuration = (secs) => {
  const s = Number(secs) || 0;
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (_e) { return iso; }
};

const STATUS_META = {
  CONNECTED: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: PhoneCall, label: 'Connected' },
  MISSED:    { color: 'bg-amber-100 text-amber-700 border-amber-200',       icon: PhoneMissed, label: 'Missed' },
  REJECTED:  { color: 'bg-rose-100 text-rose-700 border-rose-200',          icon: PhoneOff,   label: 'Rejected' },
  BUSY:      { color: 'bg-orange-100 text-orange-700 border-orange-200',    icon: Phone,      label: 'Busy' },
  NO_ANSWER: { color: 'bg-slate-100 text-slate-700 border-slate-200',       icon: PhoneOff,   label: 'No Answer' },
  FAILED:    { color: 'bg-rose-100 text-rose-700 border-rose-200',          icon: PhoneOff,   label: 'Failed' },
};

const MiniStat = ({ label, value, tone, testId }) => {
  const tones = {
    default: 'bg-muted',
    success: 'bg-emerald-50 text-emerald-700',
    warn:    'bg-amber-50 text-amber-700',
    info:    'bg-sky-50 text-sky-700',
  };
  return (
    <div className={`rounded-md p-2 text-center ${tones[tone] || tones.default}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
};

const LeadCallHistory = ({ leadId, leadName, leadMobile }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/call-tracking/lead/${leadId}/analytics`);
      setData(data);
    } catch (_e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [api, leadId]);

  useEffect(() => { load(); }, [load]);

  if (!leadId) return null;

  return (
    <div className="space-y-3" data-testid="lead-call-history">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-muted-foreground" />
          <h4 className="font-medium text-sm">Call Analytics</h4>
        </div>
        <Button size="sm" onClick={() => setLogOpen(true)} data-testid="lead-call-log-btn">
          <Plus className="h-3 w-3 mr-1" /> Log Call
        </Button>
      </div>

      {loading && !data ? (
        <p className="text-xs text-muted-foreground py-3 text-center">
          <Loader2 className="inline h-3 w-3 mr-1 animate-spin" /> Loading…
        </p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <MiniStat label="Total" value={data?.totalCalls ?? 0} testId="leadcall-stat-total" />
            <MiniStat label="Connected" value={data?.connectedCalls ?? 0} tone="success" testId="leadcall-stat-connected" />
            <MiniStat label="Talk Time" value={fmtDuration(data?.totalTalkTimeSeconds)} tone="info" testId="leadcall-stat-talktime" />
          </div>

          {data?.lastCallAt && (
            <div className="text-xs text-muted-foreground border-l-2 border-primary pl-2">
              Last call: <strong>{STATUS_META[data.lastCallStatus]?.label || data.lastCallStatus}</strong>
              {' · '}{fmtDateTime(data.lastCallAt)}
              {data.lastCallDurationSeconds ? ` · ${fmtDuration(data.lastCallDurationSeconds)}` : ''}
            </div>
          )}

          {/* Timeline */}
          <Card>
            <CardContent className="p-3">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Call History</p>
              {(!data?.calls || data.calls.length === 0) ? (
                <p className="text-xs text-muted-foreground py-3 text-center">No calls yet</p>
              ) : (
                <ul className="space-y-2 max-h-60 overflow-y-auto">
                  {data.calls.map((c) => {
                    const meta = STATUS_META[c.callStatus] || STATUS_META.FAILED;
                    const Icon = meta.icon;
                    return (
                      <li key={c.id} className="flex items-start gap-2 text-xs" data-testid={`leadcall-timeline-${c.id}`}>
                        <Icon className={`h-3.5 w-3.5 mt-0.5 ${meta.color.split(' ')[1]}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                            <span className="text-muted-foreground">{fmtDateTime(c.startTime)}</span>
                            {c.durationSeconds > 0 && (
                              <span className="font-mono text-muted-foreground">· {fmtDuration(c.durationSeconds)}</span>
                            )}
                            <span className="text-muted-foreground">· by {c.executiveName}</span>
                          </div>
                          {c.notes && <p className="text-muted-foreground mt-0.5 truncate">{c.notes}</p>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <LogCallDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        leadId={leadId}
        leadName={leadName}
        defaultMobile={leadMobile}
        onLogged={() => load()}
      />
    </div>
  );
};

export default LeadCallHistory;
